import type { MCPActionCreate, ToolHeaderValueType, ToolParamDataType, ToolParamValueType } from '@/types/tool'
import { parseJsonWithDetailedError, unknownKeyMessages } from '@/lib/json-editor-diagnostics'

type ValidationOptions = {
  partial?: boolean
}

const EXECUTION_TYPES: MCPActionCreate['execution_type'][] = ['webhook', 'mcp_server']
const TOOL_VISIBILITY_VALUES: NonNullable<MCPActionCreate['classification']>[] = ['internal', 'external', 'mixed']
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const
const HEADER_VALUE_TYPES: ToolHeaderValueType[] = ['valor', 'variable_dinamica', 'secreto']
const PARAM_TYPES: ToolParamDataType[] = ['string', 'number', 'integer', 'boolean']
const PARAM_VALUE_TYPES: ToolParamValueType[] = ['llm_prompt', 'constant', 'dynamic_variable']
const TOOL_ROOT_KEYS = [
  'execution_type',
  'name',
  'display_name',
  'description',
  'is_enabled',
  'http_config',
  'timeout_seconds',
  'retry_count',
  'mcp_url',
  'execution_class',
  'emoji',
  'intent_keywords',
  'intent_patterns',
  'input_schema',
  'output_schema',
  'instructions',
  'response_template',
  'classification',
  'request_headers',
  'path_params_schema',
  'query_params_schema',
  'request_body_schema',
  'http_method',
  'http_url',
  'mcp_enabled_remote_tools',
  'mcp_transport',
  'mcp_auth_type',
  'mcp_auth_header_name',
  'mcp_auth_secret_id',
] as const
const HTTP_CONFIG_KEYS = [
  'method',
  'url',
  'request_headers',
  'path_params_schema',
  'query_params_schema',
  'request_body_schema',
  'headers',
  'query_params',
  'body_params',
  'external_mcp',
] as const
const EXTERNAL_MCP_CONFIG_KEYS = [
  'managed',
  'server_url',
  'transport',
  'auth_type',
  'auth_header_name',
  'auth_secret_id',
  'enabled_remote_tools',
  'remote_tools_catalog',
  'remote_tool_name',
  'tool_id',
] as const
const HEADER_KEYS = ['id', 'name', 'value_type', 'value', 'variable_name', 'secret_id', 'type'] as const
const PARAM_KEYS = ['id', 'type', 'value_type', 'description', 'required', 'enum', 'constant_value', 'variable_name', 'value'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function pushError(errors: string[], message: string) {
  errors.push(message)
}

function resolveHeaderType(header: Record<string, unknown>): ToolHeaderValueType | null {
  if (typeof header.value_type === 'string' && HEADER_VALUE_TYPES.includes(header.value_type as ToolHeaderValueType)) {
    return header.value_type as ToolHeaderValueType
  }
  if (header.type === 'static') return 'valor'
  if (header.type === 'secret') return 'secreto'
  if (header.type === 'dynamic_variable') return 'variable_dinamica'
  return null
}

function validateHeaders(value: unknown, errors: string[]) {
  if (!Array.isArray(value)) {
    pushError(errors, '`http_config.request_headers` debe ser un array.')
    return
  }

  value.forEach((rawHeader, index) => {
    if (!isRecord(rawHeader)) {
      pushError(errors, `Header #${index + 1}: formato inválido.`)
      return
    }
    unknownKeyMessages(rawHeader, HEADER_KEYS, `\`http_config.request_headers[${index}]\``).forEach((message) => pushError(errors, message))

    const headerType = resolveHeaderType(rawHeader)
    const headerName = rawHeader.name
    if (typeof headerName !== 'string' || !headerName.trim()) {
      pushError(errors, `Header #${index + 1}: \`name\` es obligatorio.`)
    }
    if (!headerType) {
      pushError(errors, `Header #${index + 1}: \`value_type\` debe ser "valor"|"variable_dinamica"|"secreto" (o \`type\` "static"|"secret").`)
      return
    }

    if (headerType === 'valor' && rawHeader.value !== undefined && typeof rawHeader.value !== 'string') {
      pushError(errors, `Header #${index + 1}: \`value\` debe ser string.`)
    }
    if (headerType === 'variable_dinamica') {
      pushError(errors, `Header #${index + 1}: los headers de tipo "variable_dinamica" no están soportados todavía. Usa "valor" o "secreto".`)
    }
    if (headerType === 'secreto') {
      const secretId = rawHeader.secret_id ?? rawHeader.value
      if (secretId !== undefined && typeof secretId !== 'string') {
        pushError(errors, `Header #${index + 1}: \`secret_id\` (o \`value\`) debe ser string.`)
      } else if (typeof secretId !== 'string' || secretId.trim().length === 0) {
        pushError(errors, `Header #${index + 1}: debes seleccionar un \`secret_id\`.`)
      }
    }
  })
}

function validateParamSchema(kind: 'path_params_schema' | 'query_params_schema' | 'request_body_schema', value: unknown, errors: string[]) {
  if (!Array.isArray(value)) {
    pushError(errors, `\`http_config.${kind}\` debe ser un array.`)
    return
  }

  value.forEach((rawParam, index) => {
    if (!isRecord(rawParam)) {
      pushError(errors, `${kind} #${index + 1}: formato inválido.`)
      return
    }
    unknownKeyMessages(rawParam, PARAM_KEYS, `\`http_config.${kind}[${index}]\``).forEach((message) => pushError(errors, message))

    if (typeof rawParam.id !== 'string' || !rawParam.id.trim()) {
      pushError(errors, `${kind} #${index + 1}: \`id\` es obligatorio.`)
    }
    if (typeof rawParam.type !== 'string' || !PARAM_TYPES.includes(rawParam.type as ToolParamDataType)) {
      pushError(errors, `${kind} #${index + 1}: \`type\` debe ser "string"|"number"|"integer"|"boolean".`)
    }
    if (typeof rawParam.value_type !== 'string' || !PARAM_VALUE_TYPES.includes(rawParam.value_type as ToolParamValueType)) {
      pushError(errors, `${kind} #${index + 1}: \`value_type\` debe ser "llm_prompt"|"constant"|"dynamic_variable".`)
    }

    if (rawParam.description !== undefined && typeof rawParam.description !== 'string') {
      pushError(errors, `${kind} #${index + 1}: \`description\` debe ser string.`)
    }
    if (rawParam.required !== undefined && typeof rawParam.required !== 'boolean') {
      pushError(errors, `${kind} #${index + 1}: \`required\` debe ser boolean.`)
    }
    if (rawParam.enum !== undefined) {
      if (!isStringArray(rawParam.enum)) {
        pushError(errors, `${kind} #${index + 1}: \`enum\` debe ser array de strings.`)
      } else {
        if (rawParam.enum.some((value) => value.trim().length === 0)) {
          pushError(errors, `${kind} #${index + 1}: \`enum\` no puede contener strings vacíos.`)
        }
        if (
          rawParam.type !== 'string' &&
          rawParam.type !== 'number' &&
          rawParam.type !== 'integer'
        ) {
          pushError(
            errors,
            `${kind} #${index + 1}: \`enum\` solo aplica a parámetros de tipo \`string\`, \`number\` o \`integer\`.`
          )
        }
        if (rawParam.value_type !== 'llm_prompt') {
          pushError(errors, `${kind} #${index + 1}: \`enum\` solo aplica cuando \`value_type\` es \`llm_prompt\`.`)
        }
      }
    }

    if (rawParam.value_type === 'constant') {
      const constantValue = rawParam.constant_value ?? rawParam.value
      if (constantValue !== undefined && typeof constantValue !== 'string') {
        pushError(errors, `${kind} #${index + 1}: \`constant_value\` (o \`value\`) debe ser string.`)
      } else if (typeof constantValue !== 'string' || constantValue.trim().length === 0) {
        pushError(errors, `${kind} #${index + 1}: \`constant_value\` (o \`value\`) es obligatorio cuando \`value_type\` es \`constant\`.`)
      }
    }
    if (rawParam.value_type === 'dynamic_variable') {
      const variableName = rawParam.variable_name ?? rawParam.value
      if (variableName !== undefined && typeof variableName !== 'string') {
        pushError(errors, `${kind} #${index + 1}: \`variable_name\` (o \`value\`) debe ser string.`)
      } else if (typeof variableName !== 'string' || variableName.trim().length === 0) {
        pushError(errors, `${kind} #${index + 1}: \`variable_name\` (o \`value\`) es obligatorio cuando \`value_type\` es \`dynamic_variable\`.`)
      }
    }
  })
}

function validateHttpConfig(httpConfig: unknown, errors: string[]) {
  if (!isRecord(httpConfig)) {
    pushError(errors, '`http_config` debe ser un objeto.')
    return
  }
  unknownKeyMessages(httpConfig, HTTP_CONFIG_KEYS, '`http_config`').forEach((message) => pushError(errors, message))

  if (httpConfig.method !== undefined && (typeof httpConfig.method !== 'string' || !HTTP_METHODS.includes(httpConfig.method as typeof HTTP_METHODS[number]))) {
    pushError(errors, '`http_config.method` debe ser GET/POST/PUT/PATCH/DELETE.')
  }
  if (httpConfig.url !== undefined && typeof httpConfig.url !== 'string') {
    pushError(errors, '`http_config.url` debe ser string.')
  }
  if (httpConfig.headers !== undefined && !isRecord(httpConfig.headers)) {
    pushError(errors, '`http_config.headers` debe ser un objeto key:value.')
  }
  if (httpConfig.query_params !== undefined && !isRecord(httpConfig.query_params)) {
    pushError(errors, '`http_config.query_params` debe ser un objeto key:value.')
  }
  if (httpConfig.body_params !== undefined && !isRecord(httpConfig.body_params)) {
    pushError(errors, '`http_config.body_params` debe ser un objeto.')
  }
  if (httpConfig.external_mcp !== undefined) {
    if (!isRecord(httpConfig.external_mcp)) {
      pushError(errors, '`http_config.external_mcp` debe ser un objeto.')
    } else {
      unknownKeyMessages(httpConfig.external_mcp, EXTERNAL_MCP_CONFIG_KEYS, '`http_config.external_mcp`')
        .forEach((message) => pushError(errors, message))
      if (
        httpConfig.external_mcp.enabled_remote_tools !== undefined &&
        !isStringArray(httpConfig.external_mcp.enabled_remote_tools)
      ) {
        pushError(errors, '`http_config.external_mcp.enabled_remote_tools` debe ser array de strings.')
      }
      if (
        httpConfig.external_mcp.remote_tools_catalog !== undefined &&
        !Array.isArray(httpConfig.external_mcp.remote_tools_catalog)
      ) {
        pushError(errors, '`http_config.external_mcp.remote_tools_catalog` debe ser un array.')
      }
    }
  }

  if (httpConfig.request_headers !== undefined) {
    validateHeaders(httpConfig.request_headers, errors)
  }
  if (httpConfig.path_params_schema !== undefined) {
    validateParamSchema('path_params_schema', httpConfig.path_params_schema, errors)
  }
  if (httpConfig.query_params_schema !== undefined) {
    validateParamSchema('query_params_schema', httpConfig.query_params_schema, errors)
  }
  if (httpConfig.request_body_schema !== undefined) {
    validateParamSchema('request_body_schema', httpConfig.request_body_schema, errors)
  }
}

export function validateToolJsonPayload(value: unknown, options?: ValidationOptions): string[] {
  const errors: string[] = []
  const partial = Boolean(options?.partial)

  if (!isRecord(value)) {
    return ['El JSON debe ser un objeto.']
  }
  unknownKeyMessages(value, TOOL_ROOT_KEYS, 'raíz').forEach((message) => pushError(errors, message))

  const executionType = value.execution_type
  if (!partial || executionType !== undefined) {
    if (typeof executionType !== 'string' || !EXECUTION_TYPES.includes(executionType as MCPActionCreate['execution_type'])) {
      pushError(errors, '`execution_type` debe ser "webhook" (Webhook / HTTP Request) | "mcp_server".')
    }
  }

  if (value.name !== undefined && typeof value.name !== 'string') pushError(errors, '`name` debe ser string.')
  if (typeof value.name === 'string' && !value.name.trim()) pushError(errors, '`name` no puede estar vacío.')
  if (value.display_name !== undefined && typeof value.display_name !== 'string') pushError(errors, '`display_name` debe ser string.')
  if (typeof value.display_name === 'string' && !value.display_name.trim()) pushError(errors, '`display_name` no puede estar vacío.')
  if (value.description !== undefined && typeof value.description !== 'string') pushError(errors, '`description` debe ser string.')
  if (value.is_enabled !== undefined && typeof value.is_enabled !== 'boolean') pushError(errors, '`is_enabled` debe ser boolean.')
  if (
    value.timeout_seconds !== undefined &&
    (typeof value.timeout_seconds !== 'number' || !Number.isFinite(value.timeout_seconds) || value.timeout_seconds <= 0)
  ) {
    pushError(errors, '`timeout_seconds` debe ser número > 0.')
  }
  if (
    value.retry_count !== undefined &&
    (typeof value.retry_count !== 'number' || !Number.isFinite(value.retry_count) || value.retry_count < 0)
  ) {
    pushError(errors, '`retry_count` debe ser número >= 0.')
  }
  if (value.mcp_url !== undefined && typeof value.mcp_url !== 'string') pushError(errors, '`mcp_url` debe ser string.')
  if (value.mcp_enabled_remote_tools !== undefined && !isStringArray(value.mcp_enabled_remote_tools)) {
    pushError(errors, '`mcp_enabled_remote_tools` debe ser array de strings.')
  }
  if (
    value.mcp_transport !== undefined &&
    value.mcp_transport !== 'http_jsonrpc' &&
    value.mcp_transport !== 'streamable_http'
  ) {
    pushError(errors, '`mcp_transport` debe ser "http_jsonrpc" | "streamable_http".')
  }
  if (
    value.mcp_auth_type !== undefined &&
    value.mcp_auth_type !== 'none' &&
    value.mcp_auth_type !== 'bearer' &&
    value.mcp_auth_type !== 'header'
  ) {
    pushError(errors, '`mcp_auth_type` debe ser "none" | "bearer" | "header".')
  }
  if (value.mcp_auth_header_name !== undefined && typeof value.mcp_auth_header_name !== 'string') {
    pushError(errors, '`mcp_auth_header_name` debe ser string.')
  }
  if (value.mcp_auth_secret_id !== undefined && typeof value.mcp_auth_secret_id !== 'string') {
    pushError(errors, '`mcp_auth_secret_id` debe ser string.')
  }
  if (value.execution_class !== undefined && typeof value.execution_class !== 'string') pushError(errors, '`execution_class` debe ser string.')
  if (value.emoji !== undefined && typeof value.emoji !== 'string') pushError(errors, '`emoji` debe ser string.')

  if (value.intent_keywords !== undefined && !isStringArray(value.intent_keywords)) {
    pushError(errors, '`intent_keywords` debe ser array de strings.')
  }
  if (value.intent_patterns !== undefined && !isStringArray(value.intent_patterns)) {
    pushError(errors, '`intent_patterns` debe ser array de strings.')
  }
  if (value.instructions !== undefined && typeof value.instructions !== 'string') {
    pushError(errors, '`instructions` debe ser string.')
  }
  if (value.response_template !== undefined && typeof value.response_template !== 'string') {
    pushError(errors, '`response_template` debe ser string.')
  }
  if (
    value.classification !== undefined &&
    (typeof value.classification !== 'string' || !TOOL_VISIBILITY_VALUES.includes(value.classification as NonNullable<MCPActionCreate['classification']>))
  ) {
    pushError(errors, '`classification` debe ser "internal" | "external" | "mixed".')
  }
  if (value.input_schema !== undefined && !isRecord(value.input_schema)) {
    pushError(errors, '`input_schema` debe ser un objeto.')
  }
  if (value.output_schema !== undefined && !isRecord(value.output_schema)) {
    pushError(errors, '`output_schema` debe ser un objeto.')
  }

  if (value.http_config !== undefined) {
    validateHttpConfig(value.http_config, errors)
  }
  if (value.request_headers !== undefined) {
    validateHeaders(value.request_headers, errors)
  }
  if (value.path_params_schema !== undefined) {
    validateParamSchema('path_params_schema', value.path_params_schema, errors)
  }
  if (value.query_params_schema !== undefined) {
    validateParamSchema('query_params_schema', value.query_params_schema, errors)
  }
  if (value.request_body_schema !== undefined) {
    validateParamSchema('request_body_schema', value.request_body_schema, errors)
  }
  if (
    value.http_method !== undefined &&
    (typeof value.http_method !== 'string' || !HTTP_METHODS.includes(value.http_method as typeof HTTP_METHODS[number]))
  ) {
    pushError(errors, '`http_method` debe ser GET/POST/PUT/PATCH/DELETE.')
  }
  if (value.http_url !== undefined && typeof value.http_url !== 'string') {
    pushError(errors, '`http_url` debe ser string.')
  }

  if (executionType === 'webhook') {
    if (!isRecord(value.http_config)) {
      pushError(errors, 'Para `webhook` (Webhook / HTTP Request), `http_config` es obligatorio.')
    } else {
      if (typeof value.http_config.url !== 'string' || !value.http_config.url.trim()) {
        pushError(errors, 'Para `webhook` (Webhook / HTTP Request), `http_config.url` es obligatorio.')
      }
      if (typeof value.http_config.method !== 'string' || !HTTP_METHODS.includes(value.http_config.method as typeof HTTP_METHODS[number])) {
        pushError(errors, 'Para `webhook` (Webhook / HTTP Request), `http_config.method` es obligatorio (GET/POST/PUT/PATCH/DELETE).')
      }
    }
  } else if (executionType === 'mcp_server') {
    const hasMcpUrl = typeof value.mcp_url === 'string' && value.mcp_url.trim().length > 0
    const hasHttpUrl = isRecord(value.http_config) && typeof value.http_config.url === 'string' && value.http_config.url.trim().length > 0
    if (!hasMcpUrl && !hasHttpUrl) {
      pushError(errors, 'Para `mcp_server`, `mcp_url` (o `http_config.url`) es obligatorio.')
    }
  } else if (executionType === 'python') {
    if (typeof value.execution_class !== 'string' || !value.execution_class.trim()) {
      pushError(errors, 'Para `python`, `execution_class` es obligatorio.')
    }
  }

  return errors
}

export function validateToolJsonText(source: string): string | null {
  if (!source.trim()) return 'El JSON no puede estar vacío.'
  const parsed = parseJsonWithDetailedError(source)
  if (parsed.error) return parsed.error
  const errors = validateToolJsonPayload(parsed.value)
  return errors.length > 0 ? errors.join('\n') : null
}

export function normalizeToolJsonPayload<T extends Record<string, unknown>>(value: T): T {
  if (!isRecord(value.http_config)) return value

  const httpConfig = { ...value.http_config }
  if (Array.isArray(httpConfig.request_headers)) {
    httpConfig.request_headers = httpConfig.request_headers.map((item, index) => {
      if (!isRecord(item)) return item
      const withDefaults = { ...item }
      if (!withDefaults.id) withDefaults.id = `hdr_${index}`
      if (typeof withDefaults.value_type === 'string' && HEADER_VALUE_TYPES.includes(withDefaults.value_type as ToolHeaderValueType)) {
        return withDefaults
      }
      if (withDefaults.type === 'static') {
        return {
          ...withDefaults,
          value_type: 'valor',
          value: typeof withDefaults.value === 'string' ? withDefaults.value : '',
        }
      }
      if (withDefaults.type === 'secret') {
        const secret = withDefaults.secret_id ?? withDefaults.value
        return {
          ...withDefaults,
          value_type: 'secreto',
          secret_id: typeof secret === 'string' ? secret : '',
        }
      }
      return withDefaults
    })
  }

  return {
    ...value,
    http_config: httpConfig,
  }
}
